/**
 * Компонент сообщения чата
 */

import { useEffect, useRef } from 'react';
import styles from './ChatMessage.module.css';
import { Message } from '@/types/message.types';
import { formatTime } from '@/utils/helpers';
import { LarteLogo } from '../Logo';
import { MarkdownMessage } from '../MarkdownMessage';
import { MessageImage } from './MessageImage';
import { FallbackLeadForm } from '../FallbackLeadForm/FallbackLeadForm';
import { ErrorBoundary } from '../ErrorBoundary';
import { ActionButtons } from '../ActionButtons';

const ANCHOR_RE = /\]\(#([\w-]+)\)|(?:^|\s)#([\w-]+)/;

/**
 * Пропсы компонента ChatMessage
 */
interface ChatMessageProps {
  /** Данные сообщения */
  message: Message;
  /** Колбек отправки сообщения (для кнопок действий): apiText для n8n, displayText для чата */
  onSendMessage?: (apiText: string, displayText?: string) => void;
}

/**
 * Сообщение в чате
 * 
 * @description Отображает сообщение от пользователя или ассистента
 */
export function ChatMessage({ message, onSendMessage }: ChatMessageProps) {
  const isUser = message.sender === 'user';
  const isAssistant = message.sender === 'assistant';
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (!isAssistant || scrolledRef.current) return;
    if (Date.now() - message.timestamp > 5000) return;

    const match = message.content.match(ANCHOR_RE);
    if (!match) return;
    const anchorId = match[1] || match[2];
    if (!anchorId) return;

    scrolledRef.current = true;
    const timer = setTimeout(() => {
      const el =
        document.getElementById(anchorId) ||
        document.querySelector(`[name="${anchorId}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 500);
    return () => clearTimeout(timer);
  }, [isAssistant, message.content, message.timestamp]);

  return (
    <div
      className={`
        ${styles.messageWrapper}
        ${isUser ? styles.user : ''}
        ${isAssistant ? styles.assistant : ''}
      `}
    >
      <div className={styles.messageContainer}>
        {/* Аватар (только для assistant) */}
        {isAssistant && (
          <div className={styles.avatar}>
            <LarteLogo size={32} color="var(--accent)" />
          </div>
        )}

        {/* Bubble с текстом и файлами */}
        <div
          className={`
            ${styles.bubble}
            ${isUser ? styles.user : ''}
            ${isAssistant ? styles.assistant : ''}
          `}
        >
          {/* Файлы (изображения) - показываем ВСЕГДА если есть */}
          {message.files && message.files.length > 0 && (
            <div className={styles.files}>
              {message.files.map((file) => (
                <MessageImage key={file.id} file={file} />
              ))}
            </div>
          )}

          {/* Текст сообщения (скрываем при fallbackForm без intent — FallbackLeadForm покажет свой intro) */}
          {message.content &&
            message.content !== 'Фото' &&
            !(message.metadata?.fallbackForm && message.metadata?.leadFormReason !== 'intent_detected') && (
              <div className={styles.messageText}>
                {isAssistant ? (
                  <ErrorBoundary>
                    <MarkdownMessage content={message.content} />
                  </ErrorBoundary>
                ) : (
                  message.content
                )}
              </div>
            )}

          {/* Кнопки действий от n8n */}
          {message.metadata?.buttons && message.metadata.buttons.length > 0 && onSendMessage && (
            <ActionButtons
              buttons={message.metadata.buttons}
              onButtonClick={onSendMessage}
            />
          )}

          {/* Форма лида (при showLeadForm от AI или при ошибке API) */}
          {message.metadata?.fallbackForm && (
            <FallbackLeadForm
              reason={message.metadata?.leadFormReason}
              hideIntro={message.metadata?.leadFormReason === 'intent_detected' && !!message.content}
            />
          )}
        </div>
      </div>

      {/* Метаданные: время и статус */}
      <div className={styles.metadata}>
        <span className={styles.time}>{formatTime(message.timestamp)}</span>
        
        {/* Статус для user messages */}
        {isUser && message.status && (
          <span className={styles.status} aria-label={message.status}>
            {message.status === 'sent' && '✓'}
            {message.status === 'delivered' && '✓✓'}
            {message.status === 'error' && '⚠️'}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Typing indicator компонент
 */
export function TypingIndicator() {
  return (
    <div className={styles.typing} aria-label="Typing...">
      <span className={styles.typingDot} />
      <span className={styles.typingDot} />
      <span className={styles.typingDot} />
    </div>
  );
}

